import { albumByIdQuery, albumTrackPositionUpdate } from '@/entities/albums'
import {
  TrackUploadStatus,
  type TracksQueryModelBase,
  createTracksQueryModel,
  trackByIdUploadStatus,
  trackDelete,
  trackMultipleDelete,
  trackUpdate,
} from '@/entities/tracks'
import {
  type UploadFileWithId,
  uploadFileToPresignedUrlFx,
} from '@/features/upload-files'
import {
  type AlbumModel,
  type MultiUploadTracksRequestModel,
  type TrackModel,
  postApiTracksAlbumidMultiUpload,
} from '@/shared/api'
import { notifyMaxLengthExceeded } from '@/shared/lib/form-utils'
import { showNotificationSuccessFx } from '@/shared/lib/providers'
import {
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { previous } from 'patronum'

import { createAlbumTracksMultipleUploadModel } from './upload-multiple-track-files-to-album'

const tracksMultiUploadFx = createEffect(postApiTracksAlbumidMultiUpload)
export interface TrackFileForMultipleUpload {
  uid: string
  file: File
  params: MultiUploadTracksRequestModel
}

export type UploadTrackMultipleFilesToAlbumPayload = {
  albumId: string
  tracks: TrackFileForMultipleUpload[]
  fileStartUpload: (payload: Pick<UploadFileWithId, 'uid' | 'id'>) => void
  fileProgressChanged?: (
    payload: Pick<UploadFileWithId, 'uid' | 'percent'>,
  ) => void
  fileUploadSuccess: (payload: Pick<UploadFileWithId, 'uid' | 'url'>) => void
  fileUploadError: (payload: Pick<UploadFileWithId, 'uid' | 'error'>) => void
}

// Album Tracks List
export type AlbumTrackWithStatus = TrackModel & {
  fileUploadStatus: string
}

export const uploadTrackMultipleFilesFx = createEffect(
  async ({
    albumId,
    tracks,
    fileStartUpload,
    fileUploadSuccess,
    fileUploadError,
  }: UploadTrackMultipleFilesToAlbumPayload) => {
    try {
      const data = tracks.map((track) => track.params)
      const { files } = await tracksMultiUploadFx({ albumId, data })
      if (!files || Object.keys(files).length !== tracks.length) {
        throw new Error(
          'Некорректный ответ от сервера: presigned URL не получены для всех файлов',
        )
      }
      await Promise.all(
        tracks.map((track) => {
          const { file, trackId } = files[track.uid]
          fileStartUpload({ uid: track.uid, id: trackId })

          return uploadFileToPresignedUrlFx({
            url: file.presignedUrl,
            file: track.file,
          })
            .then(() => {
              fileUploadSuccess({
                uid: track.uid,
                url: file.fileInfo.fullCdnUrl,
              })
            })
            .then(() => {
              void trackByIdUploadStatus.start({ id: trackId })
            })
            .catch((error) => {
              fileUploadError({ uid: track.uid, error })
              throw error
            })
        }),
      )
    } catch (error) {
      notifyMaxLengthExceeded(error)
      throw error
    }
  },
)

// Submit tracks on files upload

const albumTrackMultiuploadGateOpened = createEvent<AlbumModel>()

const albumTrackMultiuploadModel = createAlbumTracksMultipleUploadModel()

sample({
  clock: albumByIdQuery.$data,
  filter: (album): album is AlbumModel => album !== null,
  target: albumTrackMultiuploadGateOpened,
})

sample({
  clock: albumTrackMultiuploadGateOpened,
  fn: (album) => ({ albumId: album.id }),
  target: albumTrackMultiuploadModel.Gate.open,
})

export const addAlbumTrackMultiuploadFiles =
  albumTrackMultiuploadModel.filesAdded

sample({
  source: albumTrackMultiuploadModel.Gate.state,
  clock: albumByIdQuery.reset,
  target: albumTrackMultiuploadModel.Gate.close,
})
// Tracks

export const tracksAlbumTracksQueryModel = createTracksQueryModel({
  initialFilters: {
    UploadStatuses: [TrackUploadStatus.Uploaded, TrackUploadStatus.Uploading],
  },
  usePagination: false,
  accumulate: false,
}) as TracksQueryModelBase
const {
  availableSet: tracksAlbumTracksAvailableSet,
  unavailableSet: tracksAlbumTracksUnavailableSet,
} = tracksAlbumTracksQueryModel
export const {
  //   Query
  query: tracksAlbumTracksQuery,
  //   Init
  filtersInit: tracksAlbumTracksFiltersInit,
  //   Refresh
  refreshed: tracksAlbumTracksRefreshed,
} = tracksAlbumTracksQueryModel

sample({
  clock: albumTrackMultiuploadModel.Gate.open,
  target: tracksAlbumTracksAvailableSet,
})
sample({
  clock: albumTrackMultiuploadModel.Gate.close,
  target: tracksAlbumTracksUnavailableSet,
})

sample({
  source: albumTrackMultiuploadModel.Gate.state,
  clock: tracksAlbumTracksAvailableSet,
  fn: (payload) => ({
    AlbumIds: [payload.albumId],
  }),
  target: tracksAlbumTracksFiltersInit,
})

// Patch Album Tracks Status

export const $uploadStatusPatches = createStore<
  Record<AlbumTrackWithStatus['id'], AlbumTrackWithStatus['uploadStatus']>
>({})
  .on(trackByIdUploadStatus.finished.success, (state, { params: { id } }) => ({
    ...state,
    [id]: TrackUploadStatus.Uploaded,
  }))
  .reset(albumTrackMultiuploadModel.Gate.close)

// Reorder Album Tracks

export const albumTracksReordered = createEvent<AlbumTrackWithStatus[]>()

const $albumTracksOrder = createStore<string[]>([])

$albumTracksOrder.on(albumTracksReordered, (_, newOrder) =>
  newOrder.map((t) => t.id),
)

export const $albumTracksReorderBlocked = createStore(false)

sample({
  clock: albumTrackPositionUpdate.start,
  fn: () => true,
  target: $albumTracksReorderBlocked,
})

sample({
  clock: albumTrackPositionUpdate.finished.finally,
  fn: () => false,
  target: $albumTracksReorderBlocked,
})

sample({
  clock: albumTrackPositionUpdate.finished.success,
  fn: () => {
    return {
      message: 'Порядок треков обновлён',
    }
  },
  target: showNotificationSuccessFx,
})

// Revert tracks in album local order on error
sample({
  clock: albumTrackPositionUpdate.finished.failure,
  source: previous($albumTracksOrder),
  filter: (order: string[] | null): order is string[] => order !== null,
  target: $albumTracksOrder,
})

export const $albumTracksWithProgressReordered = combine(
  tracksAlbumTracksQuery.$data,
  albumTrackMultiuploadModel.$files,
  $albumTracksOrder,
  $uploadStatusPatches,
  (tracks, uploads, order, patches): AlbumTrackWithStatus[] => {
    if (!tracks) return []
    const tracksWithProgress: AlbumTrackWithStatus[] = tracks.items.map((t) => {
      const upload = uploads.find((u) => u.id === t.id)
      const patchedUploadStatus = patches[t.id]
      return {
        ...t,
        key: t.id,
        fileUploadStatus: upload?.status ?? 'done',
        uploadStatus: patchedUploadStatus ?? t.uploadStatus,
      }
    })
    if (!order.length) return tracksWithProgress

    const map = new Map(tracksWithProgress.map((t) => [t.id, t]))
    const reordered = order
      .map((id) => map.get(id))
      .filter((t): t is AlbumTrackWithStatus => Boolean(t))
    const rest = tracksWithProgress.filter((t) => !order.includes(t.id))
    return [...reordered, ...rest]
  },
)

sample({
  clock: [
    tracksMultiUploadFx.done,
    trackUpdate.finished.success,
    trackDelete.finished.success,
    trackMultipleDelete.finished.success,
    albumTrackPositionUpdate.finished.success,
    albumTrackMultiuploadModel.fileUploadSuccess,
    uploadTrackMultipleFilesFx,
  ],
  target: tracksAlbumTracksRefreshed,
})
